import { formatCubicMeters } from 'utils/formatCubicMeters';

type GridCheckResult = {
  message: string;
  type: 'error' | 'warning' | 'success';
  showBillingLink: boolean;
};

type VolumeCheckParams = {
  totalVolume: number;
  volumeLimit: number | null;
  gridHeight?: number;
  gridResolution?: number;
  locale?: string;
};

type SensorPointsCheckParams = {
  totalSensorPoints: number;
  sensorPointLimit: number | null;
  locale?: string;
};

type SingleHeightSensorPointLayerParams = {
  gridHeight: number;
  gridResolution: number;
};

export const exceedsVolumeLimit = (volume: number | null | undefined, volumeLimit: number | null | undefined) =>
  volume != null &&
  volumeLimit != null &&
  Number.isFinite(volume) &&
  Number.isFinite(volumeLimit) &&
  volume > volumeLimit;

export const requiresVolumePayment = (gridVolume: number | null | undefined, paidVolume: number | null | undefined) =>
  exceedsVolumeLimit(gridVolume, Number(paidVolume ?? 0));

export function hasSingleSensorPointLayerInHeight({
  gridHeight,
  gridResolution,
}: SingleHeightSensorPointLayerParams): boolean {
  return (
    Number.isFinite(gridHeight) &&
    Number.isFinite(gridResolution) &&
    gridResolution > 0 &&
    gridHeight < 2 * gridResolution
  );
}

export function checkVolume({
  totalVolume,
  volumeLimit,
  gridHeight,
  gridResolution,
  locale = 'en-US',
}: VolumeCheckParams): GridCheckResult | null {
  const numericVolumeLimit = Number(volumeLimit);
  const numericGridResolution = Number(gridResolution);
  const hasVolumeLimit = Number.isFinite(numericVolumeLimit) && numericVolumeLimit > 0;

  if (
    hasSingleSensorPointLayerInHeight({ gridHeight: Number(gridHeight), gridResolution: numericGridResolution }) ||
    !hasVolumeLimit ||
    totalVolume <= numericVolumeLimit
  ) {
    return null;
  }

  return {
    message: `This grid is estimated at ${formatCubicMeters(totalVolume, locale)} m³, which exceeds your project limit of ${formatCubicMeters(numericVolumeLimit, locale)} m³. Upgrade your plan to work with this volume ([BILLING_LINK]), or reduce Analysis Area or Grid Height.`,
    type: 'error',
    showBillingLink: true,
  };
}

export function checkSensorPoints({
  totalSensorPoints,
  sensorPointLimit,
  locale = 'en-US',
}: SensorPointsCheckParams): GridCheckResult {
  const roundedSensorPoints = Math.round(totalSensorPoints).toLocaleString(locale);
  const numericLimit = Number(sensorPointLimit);
  const hasSensorPointLimit = Number.isFinite(numericLimit) && numericLimit > 0;
  const effectiveSensorPointLimit = hasSensorPointLimit ? numericLimit * 1.05 : null;
  const displayedSensorPointLimit = hasSensorPointLimit ? Math.round(numericLimit).toLocaleString(locale) : null;

  if (effectiveSensorPointLimit !== null && totalSensorPoints > effectiveSensorPointLimit) {
    return {
      message: `This grid is estimated at ${roundedSensorPoints} sensor points, which exceeds your project limit of ${displayedSensorPointLimit}. Upgrade your plan to work with this quantity of sensor points ([BILLING_LINK]), or reduce Analysis Area, Grid Resolution, or Grid Height.`,
      type: 'error',
      showBillingLink: true,
    };
  }

  return {
    message: '',
    type: 'success',
    showBillingLink: false,
  };
}
